import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { DocumentsApiService } from '@troyai/portal/documents/data-access';
import { getLoadingStatus, LoadingState } from '@troyai/shared/state';
import { tap } from 'rxjs';
import { AssessmentViewerApiService } from '../assessment-viewer-api.service';
import { AssessmentsViewerStateModel } from './assessments-viewer-state.model';
import {
  GetMembersWithAssessmentsList,
  GetSingleAssessment,
  SelectMemberWithAssessments,
} from './assessments-viewer.actions';

const stateDefaults = {
  membersWithAssessments: {
    result: null,
    loadingStatus: LoadingState.INITIAL,
  },
  selectedMember: {
    result: null,
    loadingStatus: LoadingState.INITIAL,
  },
  selectedMemberAssessments: {
    result: null,
    loadingStatus: LoadingState.INITIAL,
  },
  selectedAssessment: {
    result: null,
    loadingStatus: LoadingState.INITIAL,
  },
} as AssessmentsViewerStateModel;

@State<AssessmentsViewerStateModel>({
  name: 'assessmentsViewerState',
  defaults: stateDefaults,
})
@Injectable()
export class AssessmentsViewerState {
  private assessmentViewerApiService = inject(AssessmentViewerApiService);
  private documentsApiService = inject(DocumentsApiService);

  @Selector()
  static getMembersWithAssessments(state: AssessmentsViewerStateModel) {
    return state.membersWithAssessments.result;
  }

  @Selector()
  static getMembersWithAssessmentsLoadingState(state: AssessmentsViewerStateModel) {
    return getLoadingStatus(state.membersWithAssessments.loadingStatus);
  }

  @Selector()
  static getSelectedMember(state: AssessmentsViewerStateModel) {
    return state.selectedMember.result;
  }

  @Selector()
  static getSelectedMemberLoadingState(state: AssessmentsViewerStateModel) {
    return getLoadingStatus(state.selectedMember.loadingStatus);
  }

  @Selector()
  static getSelectedMemberAssessments(state: AssessmentsViewerStateModel) {
    return state.selectedMemberAssessments.result;
  }

  @Selector()
  static getSelectedMemberAssessmentsLoadingState(state: AssessmentsViewerStateModel) {
    return getLoadingStatus(state.selectedMemberAssessments.loadingStatus);
  }

  @Selector()
  static getSelectedAssessment(state: AssessmentsViewerStateModel) {
    return state.selectedAssessment.result;
  }

  @Selector()
  static getSelectedAssessmentLoadingState(state: AssessmentsViewerStateModel) {
    return getLoadingStatus(state.selectedAssessment.loadingStatus);
  }

  @Action(GetMembersWithAssessmentsList)
  getMembersWithAssessmentsList({ patchState }: StateContext<AssessmentsViewerStateModel>) {
    patchState({
      membersWithAssessments: {
        result: null,
        loadingStatus: LoadingState.LOADING,
      },
    });

    return this.assessmentViewerApiService.getAllMemberAssessments().pipe(
      tap((response) => {
        patchState({
          membersWithAssessments: {
            result: response,
            loadingStatus: LoadingState.LOADED,
          },
        });
      })
    );
  }

  @Action(SelectMemberWithAssessments)
  getMemberAssessments(
    { patchState }: StateContext<AssessmentsViewerStateModel>,
    { payload }: SelectMemberWithAssessments
  ) {
    patchState({
      selectedMember: {
        result: null,
        loadingStatus: LoadingState.LOADING,
      },
      selectedMemberAssessments: {
        result: null,
        loadingStatus: LoadingState.LOADING,
      },
    });

    return this.assessmentViewerApiService.getMemberAssessments(payload.memberGlobalId).pipe(
      tap((response) => {
        patchState({
          selectedMemberAssessments: {
            result: response,
            loadingStatus: LoadingState.LOADED,
          },
        });
      })
    );
  }

  @Action(GetSingleAssessment)
  getSingleAssessment(
    { getState, patchState }: StateContext<AssessmentsViewerStateModel>,
    { payload }: GetSingleAssessment
  ) {
    const currentAssessments = getState().selectedMemberAssessments.result;

    if (!currentAssessments) {
      return;
    }

    patchState({
      selectedAssessment: {
        result: null,
        loadingStatus: LoadingState.LOADING,
      },
    });

    const selectedAssessment = currentAssessments.find(
      (assessment) => assessment.id === payload.assessmentGlobalId
    );

    if (!selectedAssessment) {
      return;
    }

    if (payload.fileId === null) {
      patchState({
        selectedAssessment: {
          result: selectedAssessment,
          loadingStatus: LoadingState.LOADED,
        },
      });
      return;
    }

    return this.documentsApiService.getSingleDocument(payload.memberGlobalId, payload.fileId).pipe(
      tap((file) => {
        patchState({
          selectedAssessment: {
            result: {
              ...selectedAssessment,
              signed_url: file.url,
            },
            loadingStatus: LoadingState.LOADED,
          },
        });
      })
    );
  }
}
